import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminNotificationService } from '../../services/adminNotifications';
import type { AdminNotification } from '../../types/adminNotification';
import { CreateNotificationModal } from '../../components/admin/notifications/CreateNotificationModal';
import { NotificationPreviewModal } from '../../components/admin/notifications/NotificationPreviewModal';
import { NotificationStatisticsModal } from '../../components/admin/notifications/NotificationStatisticsModal';

export const AdminNotificationsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<AdminNotification | null>(null);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [filters, setFilters] = useState({ status: '', type: '', page: 1 });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminNotifications', filters],
    queryFn: () => adminNotificationService.getAll(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: adminNotificationService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
    },
  });

  const sendNowMutation = useMutation({
    mutationFn: adminNotificationService.sendNow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
    },
  });

  const handleEdit = (notification: AdminNotification) => {
    setSelectedNotification(notification);
    setIsCreateModalOpen(true);
  };

  const handleViewStats = (notification: AdminNotification) => {
    setSelectedNotification(notification);
    setIsStatsModalOpen(true);
  };

  const handlePreview = (notification: AdminNotification) => {
    setSelectedNotification(notification);
    setIsPreviewModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t('notifications.actions.delete_confirm'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleSendNow = (id: string) => {
    if (window.confirm(t('notifications.actions.send_confirm'))) {
      sendNowMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('notifications.page_title')}</h1>
          <p className="text-gray-600 mt-2">
            {t('notifications.page_subtitle')}
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn btn-primary"
        >
          {t('notifications.create_button')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          className="input"
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value, page: 1 })
          }
        >
          <option value="">{t('notifications.status_filter_all')}</option>
          <option value="draft">{t('notifications.status.draft')}</option>
          <option value="scheduled">{t('notifications.status.scheduled')}</option>
          <option value="sent">{t('notifications.status.sent')}</option>
          <option value="failed">{t('notifications.status.failed')}</option>
        </select>

        <select
          className="input"
          value={filters.type}
          onChange={(e) =>
            setFilters({ ...filters, type: e.target.value, page: 1 })
          }
        >
          <option value="">{t('notifications.type_filter_all')}</option>
          <option value="announcement">{t('notifications.types.announcement')}</option>
          <option value="maintenance">{t('notifications.types.maintenance')}</option>
          <option value="security">{t('notifications.types.security')}</option>
          <option value="marketing">{t('notifications.types.marketing')}</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-8">{t('notifications.loading')}</div>
      ) : data && data.data.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto -mx-0 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.title')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.recipients')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.created')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('notifications.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.data.map((notification) => (
                <tr key={notification.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        notification.type === 'announcement'
                          ? 'bg-blue-100 text-blue-800'
                          : notification.type === 'maintenance'
                          ? 'bg-yellow-100 text-yellow-800'
                          : notification.type === 'security'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {t(`notifications.types.${notification.type}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {notification.title.en}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        notification.status === 'sent'
                          ? 'bg-green-100 text-green-800'
                          : notification.status === 'scheduled'
                          ? 'bg-blue-100 text-blue-800'
                          : notification.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {t(`notifications.status.${notification.status}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {notification.recipients_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {(notification.status === 'draft' ||
                        notification.status === 'scheduled') && (
                        <>
                          <button
                            onClick={() => handleEdit(notification)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {t('notifications.actions.edit')}
                          </button>
                          <button
                            onClick={() => handleSendNow(notification.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            {t('notifications.actions.send_now')}
                          </button>
                        </>
                      )}
                      {notification.status !== 'draft' && (
                        <button
                          onClick={() => handlePreview(notification)}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          {t('notifications.actions.preview')}
                        </button>
                      )}
                      {notification.status === 'sent' && (
                        <button
                          onClick={() => handleViewStats(notification)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t('notifications.actions.stats')}
                        </button>
                      )}
                      {notification.status !== 'sending' && (
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('notifications.actions.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-gray-500">{t('notifications.table.empty_message')}</p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn btn-primary mt-4"
          >
            {t('notifications.table.empty_subtitle')}
          </button>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > data.per_page && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: data.last_page }, (_, i) => (
            <button
              key={i}
              className={`px-3 py-1 rounded ${
                filters.page === i + 1
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setFilters({ ...filters, page: i + 1 })}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <CreateNotificationModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedNotification(null);
        }}
        notification={selectedNotification}
      />

      {/* Preview Modal */}
      {selectedNotification && (
        <NotificationPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => {
            setIsPreviewModalOpen(false);
            setSelectedNotification(null);
          }}
          notification={selectedNotification}
        />
      )}

      {/* Statistics Modal */}
      {selectedNotification && (
        <NotificationStatisticsModal
          isOpen={isStatsModalOpen}
          onClose={() => {
            setIsStatsModalOpen(false);
            setSelectedNotification(null);
          }}
          notification={selectedNotification}
        />
      )}
    </div>
  );
};
